import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { KpiCard } from "@/lovable-demo/components/bits";
import { Button } from "@/components/ui/button";
import { fmtCurrency, fmtNumber, recordNpr } from "@/lovable-demo/lib/warranty/calc";
import { predictIssueClaims } from "@/lovable-demo/lib/warranty/claims-prediction";
import type { Claim, ProblemRecord } from "@/lovable-demo/lib/warranty/types";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ClaimsScenario } from "@/lovable-demo/components/claims-scenario";

export function ClaimsPrediction({
  claims,
  records,
}: {
  claims: Claim[];
  records: ProblemRecord[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState("scenario");
  const predictions = records.map((r) => predictIssueClaims(claims, r)).filter((p) => p !== null);
  const eligible = predictions.filter((p) => p.sufficient);
  return (
    <section className="mb-6 border-b border-border pb-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold">Six-month claims prediction</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}{" "}
          Prediction basis
        </Button>
      </div>
      <div className="mb-4 flex gap-2" role="group" aria-label="Prediction source">
        <Button
          variant={mode === "scenario" ? "default" : "outline"}
          size="sm"
          aria-pressed={mode === "scenario"}
          onClick={() => setMode("scenario")}
        >
          Illustrative scenario
        </Button>
        <Button
          variant={mode === "history" ? "default" : "outline"}
          size="sm"
          aria-pressed={mode === "history"}
          onClick={() => setMode("history")}
        >
          Recorded claims history
        </Button>
      </div>
      {mode === "scenario" ? (
        <ClaimsScenario records={records} />
      ) : (
        <>
          <p className="mb-4 text-sm text-muted-foreground">
            Estimated predictions, not guaranteed outcomes · claim-level history only · not
            AI-generated
          </p>
          {expanded && (
            <div className="mb-4 space-y-2 border-l-2 border-primary pl-4 text-sm text-muted-foreground">
              <p>
                For each issue, a straight-line trend is fitted to reported claim counts in the six
                calendar months ending with its latest report. Months without reports count as zero.
                Predictions cover the next six calendar months from today. If the latest report is
                more than two calendar months old, the estimate is withheld pending newer data.
              </p>
              <p>
                Estimated future cost = predicted claims × average claim cost in that six-month
                history. Rejected claims are excluded; approved, paid and in-review claims are
                included. Claimed cost is not necessarily paid spending.
              </p>
              <p>
                At least six claims across three months with reports are required. Negative
                predictions are floored at zero. No statistical confidence range is established;
                seasonality, reporting delays, incomplete final months and corrective-action effects
                are not modeled.
              </p>
              <p>
                Record actual cost, total exposure and remaining exposure are shown separately.
                Exposure does not generate claim counts, and the predicted cost is not added to
                exposure because it may already be included. The claim-level sample is not the full
                aggregate record population.
              </p>
            </div>
          )}
          {predictions.some((p) => p.stale) && (
            <p className="mb-4 text-sm text-warning">
              Recent claims needed: some issues have old report dates, so current six-month
              predictions are withheld rather than presented as reliable future estimates.
            </p>
          )}
          <div className="mb-4 grid gap-4 sm:grid-cols-3">
            <KpiCard
              label="Predicted claims · next 6 months"
              value={
                eligible.length
                  ? fmtNumber(eligible.reduce((s, p) => s + p.predictedClaims, 0))
                  : "—"
              }
              hint="Sum of issue-specific forecast periods"
            />
            <KpiCard
              label="Estimated future claim cost"
              value={
                eligible.length
                  ? fmtCurrency(eligible.reduce((s, p) => s + p.predictedCost, 0))
                  : "—"
              }
              hint="Additional claimed cost · not actual spending"
            />
            <KpiCard
              label="Issues with sufficient history"
              value={`${eligible.length}/${predictions.length}`}
              hint="Limited claim-level sample"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1150px] text-left text-xs">
              <thead className="border-y border-border text-muted-foreground">
                <tr>
                  {[
                    "Issue",
                    "Latest reported month",
                    "Observed claims / claimed cost",
                    "Predicted claims / period",
                    "Estimated future cost",
                    "Record actual cost",
                    "Total / remaining exposure",
                  ].map((s) => (
                    <th key={s} className="px-2 py-3 font-medium">
                      {s}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {predictions.map((p) => (
                  <tr key={p.record.id} className="align-top">
                    <td className="max-w-64 px-2 py-3">
                      <Link
                        to="/demo/problem-record/$id"
                        params={{ id: p.record.id }}
                        className="text-primary hover:underline"
                      >
                        {p.record.code} · {p.record.title}
                      </Link>
                    </td>
                    <td className="num px-2 py-3">
                      {p.latest}
                      <span className="block text-muted-foreground">
                        Data-based, not today-based
                      </span>
                    </td>
                    <td className="num px-2 py-3">
                      {p.observedClaims}
                      <span className="block">{fmtCurrency(p.observedCost)}</span>
                    </td>
                    <td className="num px-2 py-3">
                      {p.sufficient
                        ? p.predictedClaims
                        : p.stale
                          ? "Recent claims needed"
                          : "Insufficient history"}
                      <span className="block text-muted-foreground">
                        {p.future[0]?.month}
                        {p.sufficient ? ` – ${p.future.at(-1)?.month}` : ""}
                      </span>
                    </td>
                    <td className="num px-2 py-3">
                      {p.sufficient ? fmtCurrency(p.predictedCost) : "—"}
                    </td>
                    <td className="num px-2 py-3">{fmtCurrency(p.record.financial.actualCost)}</td>
                    <td className="num px-2 py-3">
                      {fmtCurrency(p.record.financial.exposure)}
                      <span className="block text-muted-foreground">
                        Remaining · {fmtCurrency(recordNpr(p.record))}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {predictions.length === 0 && (
            <p className="py-4 text-sm text-muted-foreground">
              No linked claim history available for prediction.
            </p>
          )}
        </>
      )}
    </section>
  );
}
